import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, matchPath } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CampaignProvider } from "./context/CampaignContext";
import { ThemeProvider } from "./context/ThemeContext";
import { AudioPlayerProvider } from "./context/AudioPlayerContext";
import { GlobalAudioPlayer } from "./components/audio/GlobalAudioPlayer";
import { CommandPalette } from "./components/layout/CommandPalette";
import { DiceRollerPanel } from "./components/dice/DiceRollerPanel";
import { DynamicFavicon } from "./hooks/useSiteInfo";
import { isGameMaster } from "./lib/utils";
import { useCampaign } from "./context/CampaignContext";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import CampaignsPage from "./pages/CampaignsPage";
import CampaignDetailPage from "./pages/CampaignDetailPage";
import SessionsPage from "./pages/SessionsPage";
import SessionDetailPage from "./pages/SessionDetailPage";
import QuestsPage from "./pages/QuestsPage";
import QuestDetailPage from "./pages/QuestDetailPage";
import NPCsPage from "./pages/NPCsPage";
import NPCDetailPage from "./pages/NPCDetailPage";
// LocationsPage removed — locations are now managed via the Encyclopedia (LorePage)
import MapsPage from "./pages/MapsPage";
import TravelPage from "./pages/TravelPage";
import TreasurePage from "./pages/TreasurePage";
import CombatPage from "./pages/CombatPage";
import PlayerViewPage from "./pages/PlayerViewPage";
import PlayerCombatViewPage from "./pages/PlayerCombatViewPage";
import LorePage from "./pages/LorePage";
import PlayerMapViewPage from "./pages/PlayerMapViewPage";
import CharacterSheetPage from "./pages/CharacterSheetPage";
import PlayerInventoryPage from "./pages/PlayerInventoryPage";
import GMCharactersPage from "./pages/GMCharactersPage";
import GMCharacterSheetPage from "./pages/GMCharacterSheetPage";
import TimelinePage from "./pages/TimelinePage";
import RelationshipGraphPage from "./pages/RelationshipGraphPage";
import NarrativeTimelinePage from "./pages/NarrativeTimelinePage";
import CampaignSettingsPage from "./pages/CampaignSettingsPage";

// Admin Pages
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";
import AdminCampaignsPage from "./pages/admin/AdminCampaignsPage";
import AdminAuditLogsPage from "./pages/admin/AdminAuditLogsPage";
import AdminGameSystemsPage from "./pages/admin/AdminGameSystemsPage";
import AdminSystemTemplatesPage from "./pages/admin/AdminSystemTemplatesPage";
import AdminPdfBuilderPage from "./pages/admin/AdminPdfBuilderPage";
import AdminAudioPage from "./pages/admin/AdminAudioPage";
import AdminMarketplacePage from "./pages/admin/AdminMarketplacePage";
import AdminReportsPage from "./pages/admin/AdminReportsPage";
import AdminEmailTemplatesPage from "./pages/admin/AdminEmailTemplatesPage";
import AdminBetaSignupsPage from "./pages/admin/AdminBetaSignupsPage";
import AdminSubscriptionDashboardPage from "./pages/admin/AdminSubscriptionDashboardPage";
import AdminSubscriptionPlansPage from "./pages/admin/AdminSubscriptionPlansPage";
import AdminSubscribersPage from "./pages/admin/AdminSubscribersPage";
import AdminSubscriberDetailPage from "./pages/admin/AdminSubscriberDetailPage";
import AdminTransactionsPage from "./pages/admin/AdminTransactionsPage";
import AdminWebhooksPage from "./pages/admin/AdminWebhooksPage";
import AdminSubscriptionAuditLogPage from "./pages/admin/AdminSubscriptionAuditLogPage";
import MarketplacePage from "./pages/MarketplacePage";
import TemplateDetailPage from "./pages/TemplateDetailPage";

// Subscription Pages
import SubscriptionPricingPage from "./pages/subscription/PricingPage";
import SubscriptionPage from "./pages/subscription/SubscriptionPage";
import CheckoutSuccessPage from "./pages/subscription/CheckoutSuccess";
import CheckoutCancelPage from "./pages/subscription/CheckoutCancel";

// Public Pages
import FeaturesPage from "./pages/FeaturesPage";
import PricingPage from "./pages/PricingPage";
import UpdatesPage from "./pages/UpdatesPage";
import HelpPage from "./pages/HelpPage";
import ContactPage from "./pages/ContactPage";
import MentionsLegalesPage from "./pages/MentionsLegalesPage";
import CGUPage from "./pages/CGUPage";
import CGVPage from "./pages/CGVPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Chargement...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Chargement...</div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/updates" element={<UpdatesPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
      <Route path="/cgu" element={<CGUPage />} />
      <Route path="/cgv" element={<CGVPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId" element={<ProtectedRoute><CampaignDetailPage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/sessions" element={<ProtectedRoute><SessionsPage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/sessions/:sessionId" element={<ProtectedRoute><SessionDetailPage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/quests" element={<ProtectedRoute><QuestsPage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/quests/:questId" element={<ProtectedRoute><QuestDetailPage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/npcs" element={<ProtectedRoute><NPCsPage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/npcs/:npcId" element={<ProtectedRoute><NPCDetailPage /></ProtectedRoute>} />
      {/* Locations redirected to encyclopedia filtered by location type */}
      <Route path="/campaigns/:campaignId/locations" element={<Navigate to="../lore?type=location" replace />} />
      <Route path="/campaigns/:campaignId/locations/:locationId" element={<Navigate to="../lore?type=location" replace />} />
      <Route path="/campaigns/:campaignId/maps" element={<ProtectedRoute><MapsPage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/maps/:mapId/travel-setup" element={<ProtectedRoute><TravelPage surface="map-setup" /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/travel" element={<ProtectedRoute><TravelPage surface="travel" /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/treasure" element={<ProtectedRoute><TreasurePage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/combat" element={<ProtectedRoute><CombatPage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/lore" element={<ProtectedRoute><LorePage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/character-sheet" element={<ProtectedRoute><CharacterSheetPage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/inventory" element={<ProtectedRoute><PlayerInventoryPage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/characters" element={<ProtectedRoute><GMCharactersPage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/characters/:sheetId" element={<ProtectedRoute><GMCharacterSheetPage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/timeline" element={<ProtectedRoute><TimelinePage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/graph" element={<ProtectedRoute><RelationshipGraphPage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/narrative" element={<ProtectedRoute><NarrativeTimelinePage /></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId/settings" element={<ProtectedRoute><CampaignSettingsPage /></ProtectedRoute>} />

      {/* Marketplace Routes */}
      <Route path="/marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
      <Route path="/marketplace/:templateId" element={<ProtectedRoute><TemplateDetailPage /></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
      <Route path="/admin/settings" element={<AdminRoute><AdminSettingsPage /></AdminRoute>} />
      <Route path="/admin/campaigns" element={<AdminRoute><AdminCampaignsPage /></AdminRoute>} />
      <Route path="/admin/audit-logs" element={<AdminRoute><AdminAuditLogsPage /></AdminRoute>} />
      <Route path="/admin/game-systems" element={<AdminRoute><AdminGameSystemsPage /></AdminRoute>} />
      <Route path="/admin/game-systems/:systemId/templates" element={<AdminRoute><AdminSystemTemplatesPage /></AdminRoute>} />
      <Route path="/admin/system-templates/:templateId/pdf-builder" element={<AdminRoute><AdminPdfBuilderPage /></AdminRoute>} />
      <Route path="/admin/audio" element={<AdminRoute><AdminAudioPage /></AdminRoute>} />
      <Route path="/admin/marketplace" element={<AdminRoute><AdminMarketplacePage /></AdminRoute>} />
      <Route path="/admin/reports" element={<AdminRoute><AdminReportsPage /></AdminRoute>} />
      <Route path="/admin/email-templates" element={<AdminRoute><AdminEmailTemplatesPage /></AdminRoute>} />
      <Route path="/admin/beta-signups" element={<AdminRoute><AdminBetaSignupsPage /></AdminRoute>} />

      {/* Admin Subscription Routes */}
      <Route path="/admin/subscriptions" element={<AdminRoute><AdminSubscriptionDashboardPage /></AdminRoute>} />
      <Route path="/admin/subscriptions/plans" element={<AdminRoute><AdminSubscriptionPlansPage /></AdminRoute>} />
      <Route path="/admin/subscriptions/subscribers" element={<AdminRoute><AdminSubscribersPage /></AdminRoute>} />
      <Route path="/admin/subscriptions/subscribers/:userId" element={<AdminRoute><AdminSubscriberDetailPage /></AdminRoute>} />
      <Route path="/admin/subscriptions/transactions" element={<AdminRoute><AdminTransactionsPage /></AdminRoute>} />
      <Route path="/admin/subscriptions/webhooks" element={<AdminRoute><AdminWebhooksPage /></AdminRoute>} />
      <Route path="/admin/subscriptions/audit-log" element={<AdminRoute><AdminSubscriptionAuditLogPage /></AdminRoute>} />

      {/* Subscription Routes */}
      <Route path="/subscription/pricing" element={<SubscriptionPricingPage />} />
      <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute>} />
      <Route path="/subscription/cancel" element={<ProtectedRoute><CheckoutCancelPage /></ProtectedRoute>} />

      {/* Public Player View */}
      <Route path="/combat/view/:shareCode" element={<PlayerViewPage />} />
      <Route path="/player-view/map/:token" element={<PlayerMapViewPage />} />
      <Route path="/player-view/combat/:token" element={<PlayerCombatViewPage />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const AudioPlayerGate = () => {
  const { isAuthenticated, user } = useAuth();
  const { currentCampaign } = useCampaign();
  const location = useLocation();

  if (!isAuthenticated || !user?.id) {
    return null;
  }

  const campaignMatch =
    matchPath({ path: "/campaigns/:campaignId/*" }, location.pathname)
    || matchPath({ path: "/campaigns/:campaignId" }, location.pathname);

  const campaignId = campaignMatch?.params?.campaignId;
  if (!campaignId) {
    return null;
  }

  if (!currentCampaign || currentCampaign.id !== campaignId) {
    return null;
  }

  const userRole = currentCampaign?.members?.find((member) => member.user_id === user.id)?.role;
  if (!isGameMaster(userRole)) {
    return null;
  }

  return (
    <AudioPlayerProvider>
      <GlobalAudioPlayer />
    </AudioPlayerProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CampaignProvider>
          <BrowserRouter>
            <DynamicFavicon />
            <AppRoutes />
            <CommandPalette />
            <DiceRollerPanel />
            <AudioPlayerGate />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </CampaignProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
